import { type ReactNode, useCallback, useMemo, useState } from "react";
import { DesktopHeader } from "@/app/layouts/DesktopHeader";
import { MainContent } from "@/app/layouts/layoutAccessibility";
import { DesktopAnnouncementModal } from "@/features/announce/components/DesktopAnnouncementModal";
import {
  DesktopAnnouncementModalContext,
  type DesktopAnnouncementModalController,
} from "@/features/announce/hooks/useDesktopAnnouncementModal";
import { useSettingsStore } from "@/features/settings/hooks";
import { resolveDensityAttribute } from "@/features/settings/utils/renderSettings";
import { useIsPcForced } from "@/shared/hooks/useMediaQuery";

interface Props {
  children: ReactNode;
}

/** 旧AI_BBS PC版の余白と固定ヘッダーを再現するデスクトップ枠。 */
export const DesktopLayout: React.FunctionComponent<Props> = ({
  children,
}: Props) => {
  const isPcForced = useIsPcForced();
  const displayDensity = useSettingsStore((state) => state.displayDensity);
  const headerFixed = useSettingsStore((state) => state.headerFixed);
  const replyButtonFixed = useSettingsStore((state) => state.replyButtonFixed);
  const [announcementModalRequest, setAnnouncementModalRequest] = useState<{
    announcementId?: number;
  } | null>(null);
  const openAnnouncementModal = useCallback((announcementId?: number): void => {
    setAnnouncementModalRequest({ announcementId });
  }, []);
  const announcementModalController =
    useMemo<DesktopAnnouncementModalController>(
      () => ({ openAnnouncementModal }),
      [openAnnouncementModal],
    );

  return (
    <DesktopAnnouncementModalContext value={announcementModalController}>
      <div
        className="desktop-app min-h-screen bg-background text-foreground"
        data-force-pc={isPcForced || undefined}
        data-density={resolveDensityAttribute(displayDensity)}
        data-header-fixed={headerFixed || undefined}
        data-reply-button-flow={!replyButtonFixed || undefined}
      >
        <DesktopHeader />
        <MainContent className="desktop-main">{children}</MainContent>
        {announcementModalRequest && (
          <DesktopAnnouncementModal
            isOpen
            initialAnnouncementId={announcementModalRequest.announcementId}
            onClose={(): void => setAnnouncementModalRequest(null)}
          />
        )}
      </div>
    </DesktopAnnouncementModalContext>
  );
};
